import { Columns3 } from 'lucide-react'
import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'

export interface ColumnDef<K extends string> {
  key: K
  label: string
  /** Stĺpce, ktoré nesú identitu riadku - tie sa vypnúť nedajú. */
  fixed?: boolean
}

/**
 * Zapínanie a vypínanie stĺpcov tabuľky.
 *
 * Voľba sa pamätá v `localStorage` pod `storageKey`. Tabuľka so všetkými
 * údajmi je na notebooku širšia než obrazovka, a operátor spravidla potrebuje
 * vidieť naraz iné stĺpce než ten, kto s tou istou obrazovkou robí uzávierku.
 */
export function useVisibleColumns<K extends string>(
  storageKey: string,
  columns: ReadonlyArray<ColumnDef<K>>,
  defaults: ReadonlyArray<K>,
) {
  const [visible, setVisible] = useState<Set<K>>(() => {
    const stored = localStorage.getItem(storageKey)
    if (!stored) return new Set(defaults)
    try {
      const parsed = JSON.parse(stored) as K[]
      // Zoznam stĺpcov sa časom mení; uložené kľúče, ktoré už neexistujú, sa
      // zahodia, inak by zostali navždy visieť v pamäti prehliadača.
      const known = new Set(columns.map((c) => c.key))
      return new Set(parsed.filter((key) => known.has(key)))
    } catch {
      return new Set(defaults)
    }
  })

  useEffect(() => {
    localStorage.setItem(storageKey, JSON.stringify([...visible]))
  }, [storageKey, visible])

  const toggle = (key: K) =>
    setVisible((current) => {
      const next = new Set(current)
      if (next.has(key)) next.delete(key)
      else next.add(key)
      return next
    })

  const isVisible = (key: K) =>
    visible.has(key) || Boolean(columns.find((c) => c.key === key)?.fixed)

  return { visible, toggle, isVisible, reset: () => setVisible(new Set(defaults)) }
}

export default function ColumnPicker<K extends string>({
  columns,
  isVisible,
  onToggle,
  onReset,
}: {
  columns: ReadonlyArray<ColumnDef<K>>
  isVisible: (key: K) => boolean
  onToggle: (key: K) => void
  onReset: () => void
}) {
  const { t } = useTranslation()
  const [open, setOpen] = useState(false)
  const box = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!open) return
    const close = (event: MouseEvent) => {
      if (box.current && !box.current.contains(event.target as Node)) setOpen(false)
    }
    const escape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setOpen(false)
    }
    document.addEventListener('mousedown', close)
    document.addEventListener('keydown', escape)
    return () => {
      document.removeEventListener('mousedown', close)
      document.removeEventListener('keydown', escape)
    }
  }, [open])

  const shown = columns.filter((column) => isVisible(column.key)).length

  return (
    <div className="relative" ref={box}>
      <button
        type="button"
        className="btn-secondary"
        onClick={() => setOpen((value) => !value)}
        aria-expanded={open}
        title={t('global.columns')}
      >
        <Columns3 size={15} />
        {t('global.columns')}
        <span className="text-slate-400">
          {shown}/{columns.length}
        </span>
      </button>

      {open ? (
        <div className="absolute right-0 z-30 mt-1 w-56 rounded-md border border-slate-200 bg-white py-1 shadow-lg">
          {columns.map((column) => (
            <label
              key={column.key}
              className="flex cursor-pointer items-center gap-2 px-3 py-1 text-sm hover:bg-slate-50"
            >
              <input
                type="checkbox"
                checked={isVisible(column.key)}
                disabled={column.fixed}
                onChange={() => onToggle(column.key)}
                className="text-brand-500 disabled:opacity-40"
              />
              <span className={column.fixed ? 'text-slate-400' : undefined}>{column.label}</span>
            </label>
          ))}
          <div className="mt-1 border-t border-slate-100 px-3 pt-1">
            <button
              type="button"
              className="text-xs text-brand-600 hover:underline"
              onClick={onReset}
            >
              {t('global.resetColumns')}
            </button>
          </div>
        </div>
      ) : null}
    </div>
  )
}
